// filepath: /Users/mauricio/codebase/unitedrentals/innovation-service-manual-tool-fe/src/app/services/websocket/websocket.service.ts
import { Injectable } from '@angular/core';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import { UserState } from '../../store/user/user.model';
import { ChatState } from '../../store/chat/chat.model';
import { Observable, Subject, timer } from 'rxjs';
import {takeUntil, take } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpRequestService } from '../../utilities/http/request.utilities';
import { AppLauncherInfo } from '../../store/appLauncher/appLauncher.model';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private socket$: WebSocketSubject<any> | null = null;
  private transactionId: string | null = null;
  private messages$ = new Subject<any>();
  private connectionTimeoutMs = environment.websocketTimeoutMs;
  private connectionClosed$ = new Subject<void>();
  private lastMessageText: string | null = null;
  private isFinalReceived = false;

  constructor(private store: Store<{ user: UserState, chat: ChatState, appLauncher: AppLauncherInfo }>, private http: HttpClient, private httpRequestService: HttpRequestService) {

  }

  createWebSocketService() {
    this.store.select(state => ({
      transactionId: state.chat.chat?.transactionId,
      accessToken: state.user.userInfo?.accessToken,
      apiKey: state.appLauncher?.api_key,
      user: state.appLauncher?.user
    }))
    .pipe(take(1))
    .subscribe(({ transactionId, accessToken, apiKey, user }) => {
      if ((transactionId && accessToken) || (transactionId &&  apiKey && user)) {
        this.transactionId = transactionId;
        this.connectWebSocket(transactionId, accessToken || '', apiKey, user);
      }
    });
  }

  private connectWebSocket(transactionId: string, accessToken: string,  apiKey: string, user: string) {
    let queryParams = ''
    if (apiKey && user) {
      queryParams = `?transactionId=${transactionId}&Authorization=123&Source=Punchout`;
    }else{
      queryParams = `?transactionId=${transactionId}&Authorization=${accessToken}`;
    }
    const websocketUrl = `${environment.integrations.chatSocket}${queryParams}`;
    console.log('Passing this is the websocket url: ' + websocketUrl)
    this.socket$ = webSocket(websocketUrl);

    console.log("[WebsocketService] Connected to websocket!");
    const messageTimeout = timer(this.connectionTimeoutMs).pipe(takeUntil(this.messages$));
    messageTimeout.subscribe(() => {
      console.warn("[WebsocketService] No messages received in 15s. Closing socket and fetching from DB...");
      this.closeWebSocket();
      this.fetchFallbackMessage(transactionId);
    });
    this.socket$.subscribe({
      next: (msg) => {
        if (msg && !msg.response && !msg.is_final) {
          msg.response = '';
        }
        if (msg?.is_final) {
          this.isFinalReceived = true;
        }
        this.messages$.next(msg);
      },
      error: (err) => {
        console.error("[WebsocketService] WebSocket error:", err);
        if (!this.isFinalReceived) {
          this.fetchFallbackMessage(transactionId);
        }
      },
      complete: () => {
        console.log("[WebsocketService] WebSocket connection closed.");
        if (!this.isFinalReceived) {
          this.fetchFallbackMessage(transactionId);
        }
      }
    });
  }

  private async fetchFallbackMessage(transactionId: string): Promise<any> {
    const accessToken = await this.store.select(state => state.user.userInfo?.accessToken).pipe(take(1)).toPromise();
    const userEmail = await this.store.select(state => state.user.userInfo?.email).pipe(take(1)).toPromise();
    const api_key = await this.store.select(state2 => state2.appLauncher?.api_key).pipe(take(1)).toPromise();
    const user = await this.store.select(state2 => state2.appLauncher?.user).pipe(take(1)).toPromise();
    console.log(accessToken)
    console.log(api_key)
    console.log(user)
    if (!accessToken && !(api_key && user)) {
      console.error("Access token not available for fallback.");
      return null;
    }
    const url = `${environment.integrations.transactionFeedback}/${transactionId}`;
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Accept', 'application/json');
    if(api_key) {
      headers = headers.append('X-Api-User', user||'');
      headers = headers.append('X-Api-Key', api_key);
    }
    else {
      headers = headers.append('Authorization',`Bearer ${accessToken}`);
      headers = headers.append('X-Api-User', userEmail||'');
    }
    // const headers = { 'Authorization': `Bearer ${accessToken}` };
    return new Promise((resolve) => {
      this.httpRequestService.request<any>('GET', url, { headers }).subscribe({
        next: (response) => {
          // Handle case where response is null or undefined
          console.log("[WebsocketService] Fallback response:", response);
          if (!response) {
            console.warn("[WebsocketService] Received empty response from fallback endpoint");
            const defaultMsg = "We're unable to generate a response from the manuals at this time. Please use the resource links above to view the manuals directly.";
            const fallbackResponse = {
              transaction_id: transactionId,
              response: defaultMsg,
              message: defaultMsg,
              is_final: true
            };
            if (this.lastMessageText !== defaultMsg) {
              this.lastMessageText = defaultMsg;
              this.messages$.next(fallbackResponse);
            }
            resolve(fallbackResponse);
            return;
          }
          
          // Safely access response properties
          const originalMessage = response.llm_response || '';
          const safeMessage = originalMessage && typeof originalMessage === 'string' && originalMessage.includes('Error processing document')
            ? "We're unable to generate a response from the manuals at this time. Please use the resource links above to view the manuals directly."
            : originalMessage || 'No response available';
          
          const messageToSend = {
            ...response,
            response: safeMessage,
            is_final: true
          };
          
          if (this.lastMessageText !== safeMessage) {
            this.lastMessageText = safeMessage;
            this.messages$.next(messageToSend);
          }
          resolve(messageToSend);
        },
        error: (error) => {
          console.error("[WebsocketService] Failed to fetch fallback message:", error);
          const defaultMessage = "We're unable to generate a response from the manuals at this time. Please use the resource links above to view the manuals directly.";
          const fallback = {
            transaction_id: transactionId,
            message: defaultMessage,
            response: defaultMessage,
            is_final: true
          };
          if (this.lastMessageText !== defaultMessage) {
            this.lastMessageText = defaultMessage;
            this.messages$.next(fallback);
          }
          resolve(fallback);
        }
      });
    });
  }

  private closeWebSocket() {
    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
      this.connectionClosed$.next();
    }
  }

  public getMessages(): Observable<any> {
    return this.messages$.asObservable();
  }


  public async postRatingData(ratingData: any): Promise<any> {
    if (!this.transactionId) {
      throw new Error('Transaction ID not available.');
    }
    const url = `${environment.integrations.transactionFeedback}/${this.transactionId}`;
    let accessToken: string | null = null;
    this.store.select(state => state.user.userInfo?.accessToken).subscribe(token => {
      accessToken = token ?? null;
    });

    if (!accessToken) {
      throw new Error('Access token not available.');
    }

    const headers = { 'Authorization': `Bearer ${accessToken}` };
    try {
      return await new Promise((resolve, reject) =>
        this.http.post<any>(url, ratingData, { headers }).subscribe({
          next: (response) => {
            resolve(response);
          },
          error: (error) => {
            console.error('Failed to post rating data', error);
            reject(null);
          }
        }));
    } catch (error) {
      console.error('Failed to post rating data', error);
      return null;
    }
  }
}
